import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchBookingsList } from '../api/bookingApi';
import { getAssetById } from '../api/getAssetById';
import type { QuickBookingDraft, QuickBookingMode } from '../booking/BookingCreateContext';
import { useI18n } from '../i18n';
import type { AppLanguage } from '../i18n/types';
import { mergeBookingAssetSnapshot } from '../utils/assetBookability';
import { assetRecordMutationId } from '../utils/assetRecordPrimaryId';
import { registerAssetVenueRecord } from '../utils/assetThumbnailStore';
import { normalizeBookingListRecords } from '../utils/bookingListNormalize';
import { assetBookingShowsPeopleCount } from '../utils/assetBookingPeopleCount';
import { resolveBookingQuickPrices } from '../utils/assetBookingPrices';
import {
  isPastCalendarDayKey,
  isoToCalendarDayKey,
  parseCalendarDayKey,
  todayCalendarDayKey,
  toCalendarDayKey,
} from '../utils/assetBookingCalendar';
import {
  bookableHourEndOptions,
  bookableHourStartOptions,
  collectDailyFullyBookedDayKeys,
  collectHourlyFullyBookedDayKeys,
  defaultBookableHourRange,
  isCalendarDaySelectable,
} from '../utils/assetQuickBookingAvailability';
import { formatMoneyAmount, type AssetLocaleCurrency } from '../utils/assetCountryCurrency';
import { applyBookingVat } from '../utils/bookingVat';
import { coerceBookingWireTotalPrice } from '../utils/bookingRecordMutate';
import { validateBookingCreateSchedule } from '../utils/bookingScheduleDefaults';
import { localizeBookingScheduleError } from '../utils/bookingScheduleErrorLabels';
import {
  availableBookingPeopleCapacity,
  assetRecordMaxCapacity,
  validateBookingPeopleCapacity,
} from '../utils/bookingPeopleCapacityValidate';
import { scheduleScrollPanelToElement } from '../utils/panelFocusScroll';
import { computeBookingTotalPrice } from '../utils/bookingPeriodEndDate';
import {
  buildDailyStartEndIso,
  buildLocalStartEndIso,
  calendarDayIsClosed,
  countWholeHoursBetween,
  isDailyRangeWithinOpeningHours,
  isHourlyRangeWithinOpeningHours,
} from '../utils/openingHoursBookingValidate';
import {
  endDayKeyForDailyCount,
  endDayKeyForMonthlyCount,
} from '../utils/bookingQuickScheduleIso';
import { BookingDatePickerField, type BookingDayAvailability } from './BookingDatePickerField';
import { RoundHourTimeInput } from './RoundHourTimeInput';
import styles from './AssetQuickBookingPanel.module.css';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

type AssetQuickBookingPanelProps = {
  asset: Record<string, unknown>;
  currency: AssetLocaleCurrency;
  disabled?: boolean;
  disabledTitle?: string;
  onProceed: (draft: QuickBookingDraft) => void;
  onCancel?: () => void;
  /** Assistant/search prefill: local calendar day `YYYY-MM-DD`. */
  initialDayKey?: string | null;
  /** Assistant/search prefill: `HH:mm` (floored to a whole hour in the picker). */
  initialStartTime?: string | null;
};

function parseBoundedInteger(raw: string, min: number, max: number, fallback: number): number {
  const n = Math.floor(Number(raw.trim()));
  if (!Number.isFinite(n) || n < min) {
    return fallback;
  }
  return Math.min(max, n);
}

function peopleLabel(count: number, language: AppLanguage): string {
  if (language === 'he') {
    return `${count} אנשים`;
  }
  return count === 1 ? `${count} person` : `${count} people`;
}

function floorStartToHour(timeHm: string | null | undefined): string | null {
  const raw = timeHm?.trim() ?? '';
  const m = /^(\d{2}):(\d{2})$/.exec(raw) || /^(\d{2})$/.exec(raw);
  if (!m) {
    return null;
  }
  return `${m[1]}:00`;
}

function hourPlusOne(startHm: string): string {
  const hour = Number(startHm.slice(0, 2));
  if (!Number.isFinite(hour)) {
    return '10:00';
  }
  return `${String(Math.min(23, hour + 1)).padStart(2, '0')}:00`;
}

function firstSelectableDayKey(
  asset: Record<string, unknown>,
  mode: QuickBookingMode,
  bookings: Record<string, unknown>[],
  assetId: string,
  fromKey: string,
): string | null {
  const parsed = parseCalendarDayKey(fromKey);
  if (!parsed) {
    return null;
  }
  const start = new Date(parsed.year, parsed.monthIndex, parsed.day);
  for (let offset = 0; offset < 730; offset += 1) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + offset);
    const key = toCalendarDayKey(d.getFullYear(), d.getMonth(), d.getDate());
    if (isCalendarDaySelectable(asset, key, mode, bookings, assetId)) {
      return key;
    }
  }
  return null;
}

export function AssetQuickBookingPanel({
  asset,
  currency,
  disabled = false,
  disabledTitle,
  onProceed,
  onCancel,
  initialDayKey = null,
  initialStartTime = null,
}: AssetQuickBookingPanelProps) {
  const { t, language } = useI18n();
  // List/search snapshots often omit opening_times (blackouts). Refresh from GET when possible.
  const [scheduleAsset, setScheduleAsset] = useState(asset);
  const prices = useMemo(() => resolveBookingQuickPrices(scheduleAsset), [scheduleAsset]);
  const monthlyAvailable = prices.monthly !== null;
  const showPeopleCount = useMemo(() => assetBookingShowsPeopleCount(scheduleAsset), [scheduleAsset]);
  const assetId = assetRecordMutationId(scheduleAsset) ?? assetRecordMutationId(asset) ?? '';

  const [bookings, setBookings] = useState<Record<string, unknown>[]>([]);
  const [mode, setMode] = useState<QuickBookingMode>('hour');
  const [dayKey, setDayKey] = useState<string | null>(
    () => initialDayKey?.trim() || todayCalendarDayKey(),
  );
  const [startTime, setStartTime] = useState(() => floorStartToHour(initialStartTime) || '09:00');
  const [endTime, setEndTime] = useState(() => {
    const start = floorStartToHour(initialStartTime) || '09:00';
    return hourPlusOne(start);
  });
  const [dayCountInput, setDayCountInput] = useState('1');
  const [monthCountInput, setMonthCountInput] = useState('1');
  const [peopleInput, setPeopleInput] = useState('1');
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setScheduleAsset(asset);
  }, [asset]);

  useEffect(() => {
    // Bring the booking form into view when it mounts under a long asset profile.
    return scheduleScrollPanelToElement({ current: null }, panelRef, 12);
  }, []);

  useEffect(() => {
    if (!assetId) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const fresh = await getAssetById(assetId);
        if (cancelled || !isPlainObject(fresh)) {
          return;
        }
        setScheduleAsset((prev) => mergeBookingAssetSnapshot(prev, fresh));
      } catch {
        /* keep snapshot when GET fails */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  useEffect(() => {
    if (!error) {
      return;
    }
    return scheduleScrollPanelToElement(panelRef, errorRef);
  }, [error]);

  const dayCount = useMemo(
    () => parseBoundedInteger(dayCountInput, 1, 30, 1),
    [dayCountInput],
  );
  const monthCount = useMemo(
    () => parseBoundedInteger(monthCountInput, 1, 24, 1),
    [monthCountInput],
  );
  const people = useMemo(() => parseBoundedInteger(peopleInput, 1, 99, 1), [peopleInput]);

  useEffect(() => {
    if (mode === 'month' && !monthlyAvailable) {
      setMode('hour');
    }
  }, [mode, monthlyAvailable]);

  useEffect(() => {
    if (!assetId) {
      setBookings([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await fetchBookingsList();
        if (!cancelled) {
          setBookings(normalizeBookingListRecords(data));
        }
      } catch {
        if (!cancelled) {
          setBookings([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  const fullyBookedDayKeys = useMemo(() => {
    if (!assetId) {
      return new Set<string>();
    }
    return mode === 'day' || mode === 'month'
      ? collectDailyFullyBookedDayKeys(bookings, assetId)
      : collectHourlyFullyBookedDayKeys(scheduleAsset, bookings, assetId);
  }, [scheduleAsset, assetId, bookings, mode]);

  const dayAvailability = useCallback(
    (key: string): BookingDayAvailability => {
      if (isPastCalendarDayKey(key)) {
        return 'past';
      }
      if (calendarDayIsClosed(scheduleAsset, key)) {
        return 'closed';
      }
      if (fullyBookedDayKeys.has(key)) {
        return 'fullyBooked';
      }
      if (!assetId || !isCalendarDaySelectable(scheduleAsset, key, mode, bookings, assetId)) {
        return 'fullyBooked';
      }
      return 'available';
    },
    [scheduleAsset, assetId, bookings, fullyBookedDayKeys, mode],
  );

  const selectedDayUnavailable = Boolean(dayKey && dayAvailability(dayKey) !== 'available');

  const applyDaySelection = useCallback(
    (nextDay: string) => {
      setDayKey(nextDay);
      setError(null);
      if (mode !== 'hour' || !assetId) {
        return;
      }
      const defaults = defaultBookableHourRange(scheduleAsset, nextDay, bookings, assetId);
      if (defaults) {
        setStartTime(defaults.start);
        setEndTime(defaults.end);
      }
    },
    [scheduleAsset, assetId, bookings, mode],
  );

  useEffect(() => {
    if (!assetId || !dayKey) {
      return;
    }
    if (dayAvailability(dayKey) === 'available') {
      return;
    }
    const replacement = firstSelectableDayKey(scheduleAsset, mode, bookings, assetId, dayKey);
    if (replacement && replacement !== dayKey) {
      applyDaySelection(replacement);
    }
  }, [applyDaySelection, scheduleAsset, assetId, bookings, dayAvailability, dayKey, mode]);

  useEffect(() => {
    if (mode !== 'hour' || !dayKey || !assetId) {
      return;
    }
    const starts = bookableHourStartOptions(scheduleAsset, dayKey, bookings, assetId);
    if (starts.length === 0) {
      return;
    }
    if (!starts.includes(startTime)) {
      setStartTime(starts[0]);
    }
  }, [scheduleAsset, assetId, bookings, dayKey, mode, startTime]);

  useEffect(() => {
    if (mode !== 'hour' || !dayKey || !assetId) {
      return;
    }
    const ends = bookableHourEndOptions(scheduleAsset, dayKey, startTime, bookings, assetId);
    if (ends.length === 0) {
      return;
    }
    if (!ends.includes(endTime)) {
      setEndTime(ends[0]);
    }
  }, [scheduleAsset, assetId, bookings, dayKey, endTime, mode, startTime]);

  const startHourOptions = useMemo(() => {
    if (!dayKey || !assetId || mode !== 'hour') {
      return [];
    }
    return bookableHourStartOptions(scheduleAsset, dayKey, bookings, assetId);
  }, [scheduleAsset, assetId, bookings, dayKey, mode]);

  const endHourOptions = useMemo(() => {
    if (!dayKey || !assetId || mode !== 'hour') {
      return [];
    }
    return bookableHourEndOptions(scheduleAsset, dayKey, startTime, bookings, assetId);
  }, [scheduleAsset, assetId, bookings, dayKey, mode, startTime]);

  const totalHours = useMemo(() => {
    if (mode !== 'hour') {
      return 0;
    }
    return countWholeHoursBetween(startTime, endTime);
  }, [mode, startTime, endTime]);

  const scheduleIso = useMemo(() => {
    if (!dayKey) {
      return null;
    }
    if (mode === 'hour') {
      return buildLocalStartEndIso(dayKey, startTime, endTime);
    }
    if (mode === 'month') {
      const endDay = endDayKeyForMonthlyCount(dayKey, monthCount);
      if (!endDay) {
        return null;
      }
      return buildDailyStartEndIso(dayKey, endDay);
    }
    const endDay = endDayKeyForDailyCount(dayKey, dayCount);
    if (!endDay) {
      return null;
    }
    return buildDailyStartEndIso(dayKey, endDay);
  }, [dayKey, mode, startTime, endTime, dayCount, monthCount]);

  const peopleCapacityAvailable = useMemo(() => {
    if (!showPeopleCount || !assetId || !scheduleIso) {
      return assetRecordMaxCapacity(scheduleAsset);
    }
    return availableBookingPeopleCapacity({
      asset: scheduleAsset,
      bookings,
      assetId,
      startIso: scheduleIso.startIso,
      endIso: scheduleIso.endIso,
    });
  }, [showPeopleCount, assetId, scheduleIso, scheduleAsset, bookings]);

  const peopleInputMax = useMemo(() => {
    if (peopleCapacityAvailable !== null && peopleCapacityAvailable >= 1) {
      return Math.min(99, peopleCapacityAvailable);
    }
    return 99;
  }, [peopleCapacityAvailable]);

  const subtotal = useMemo(() => {
    if (!scheduleIso) {
      return null;
    }
    let base: number | null = null;
    if (mode === 'hour') {
      if (totalHours < 1) {
        return null;
      }
      base = computeBookingTotalPrice(prices.hourly, totalHours);
    } else if (mode === 'month') {
      if (prices.monthly === null) {
        return null;
      }
      base = computeBookingTotalPrice(prices.monthly, monthCount);
    } else {
      base = computeBookingTotalPrice(prices.daily, dayCount);
    }
    if (base === null) {
      return null;
    }
    const scaled = showPeopleCount ? base * people : base;
    return coerceBookingWireTotalPrice(scaled);
  }, [scheduleIso, mode, totalHours, dayCount, monthCount, prices, showPeopleCount, people]);

  const payableTotal = useMemo(() => applyBookingVat(subtotal), [subtotal]);

  const openingHoursError = useMemo(() => {
    if (!dayKey || !scheduleIso) {
      return null;
    }
    if (selectedDayUnavailable || calendarDayIsClosed(scheduleAsset, dayKey)) {
      return mode === 'hour'
        ? t('quickBooking.errorClosedHours')
        : mode === 'month'
          ? t('quickBooking.errorClosedMonth')
          : t('quickBooking.errorClosedDay');
    }
    if (mode === 'hour') {
      if (totalHours < 1) {
        return t('quickBooking.errorHours');
      }
      if (startHourOptions.length === 0 || endHourOptions.length === 0) {
        return t('quickBooking.errorFullyBooked');
      }
      if (!isHourlyRangeWithinOpeningHours(scheduleAsset, dayKey, startTime, endTime)) {
        return t('quickBooking.errorClosedHours');
      }
      return null;
    }
    const endDayKey = isoToCalendarDayKey(scheduleIso.endIso) ?? scheduleIso.endIso.slice(0, 10);
    if (!isDailyRangeWithinOpeningHours(scheduleAsset, dayKey, endDayKey)) {
      return mode === 'month' ? t('quickBooking.errorClosedMonth') : t('quickBooking.errorClosedDay');
    }
    if (fullyBookedDayKeys.has(dayKey)) {
      return t('quickBooking.errorFullyBooked');
    }
    return null;
  }, [
    scheduleAsset,
    dayKey,
    scheduleIso,
    mode,
    startTime,
    endTime,
    totalHours,
    startHourOptions.length,
    endHourOptions.length,
    fullyBookedDayKeys,
    selectedDayUnavailable,
    t,
  ]);

  const handleProceed = () => {
    if (disabled || !assetId || !dayKey || !scheduleIso || subtotal === null || selectedDayUnavailable) {
      return;
    }
    if (showPeopleCount) {
      setPeopleInput(String(parseBoundedInteger(peopleInput, 1, peopleInputMax, 1)));
    }
    if (mode === 'day') {
      setDayCountInput(String(parseBoundedInteger(dayCountInput, 1, 30, 1)));
    }
    if (mode === 'month') {
      setMonthCountInput(String(parseBoundedInteger(monthCountInput, 1, 24, 1)));
    }
    if (openingHoursError) {
      setError(openingHoursError);
      return;
    }
    const scheduleErr = validateBookingCreateSchedule(scheduleIso.startIso, scheduleIso.endIso, {
      hourly: mode === 'hour',
      sameDayHourly: mode === 'hour',
    });
    if (scheduleErr) {
      setError(localizeBookingScheduleError(scheduleErr, t));
      return;
    }
    const requestedPeople = showPeopleCount ? people : 1;
    const capacityAvailable = validateBookingPeopleCapacity({
      asset: scheduleAsset,
      bookings,
      assetId,
      startIso: scheduleIso.startIso,
      endIso: scheduleIso.endIso,
      requestedPeople,
    });
    if (capacityAvailable !== null) {
      setError(t('bookings.error.peopleExceedsCapacity', { capacity: String(capacityAvailable) }));
      return;
    }
    const pricingTypeId =
      mode === 'hour'
        ? prices.hourlyPricingTypeId
        : mode === 'month'
          ? prices.monthlyPricingTypeId
          : prices.dailyPricingTypeId;
    registerAssetVenueRecord(scheduleAsset);
    onProceed({
      assetId,
      mode,
      nonce: Date.now(),
      assetSnapshot: scheduleAsset,
      createBody: {
        asset_id: assetId,
        start_date: scheduleIso.startIso,
        end_date: scheduleIso.endIso,
        amount_of_people: showPeopleCount ? people : 1,
        pricing_type_id: pricingTypeId,
        total_price: subtotal,
        addons: [],
      },
    });
  };

  const priceDisplay =
    payableTotal !== null ? formatMoneyAmount(payableTotal, currency) : t('quickBooking.pricePending');

  return (
    <section
      ref={panelRef}
      className={styles.panel}
      aria-label={t('quickBooking.title')}
      tabIndex={-1}
    >
      <div className={styles.titleRow}>
        <h2 className={styles.title}>{t('quickBooking.title')}</h2>
        {onCancel ? (
          <button type="button" className={styles.cancelBtn} onClick={onCancel}>
            {t('common.cancel')}
          </button>
        ) : null}
      </div>

      <div className={styles.modeRow} role="group" aria-label={t('quickBooking.modeLabel')}>
        <button
          type="button"
          className={[styles.modeBtn, mode === 'hour' ? styles.modeBtnActive : ''].filter(Boolean).join(' ')}
          disabled={disabled}
          onClick={() => {
            setMode('hour');
            setError(null);
          }}
        >
          {t('quickBooking.modeHour')}
        </button>
        <button
          type="button"
          className={[styles.modeBtn, mode === 'day' ? styles.modeBtnActive : ''].filter(Boolean).join(' ')}
          disabled={disabled}
          onClick={() => {
            setMode('day');
            setError(null);
          }}
        >
          {t('quickBooking.modeDay')}
        </button>
        {monthlyAvailable ? (
          <button
            type="button"
            className={[styles.modeBtn, mode === 'month' ? styles.modeBtnActive : ''].filter(Boolean).join(' ')}
            disabled={disabled}
            onClick={() => {
              setMode('month');
              setError(null);
            }}
          >
            {t('quickBooking.modeMonth')}
          </button>
        ) : null}
      </div>

      <BookingDatePickerField
        dayKey={dayKey}
        onDayKeyChange={applyDaySelection}
        language={language}
        label={t('quickBooking.selectDate')}
        dayColumnLabel={t('quickBooking.dayColumn')}
        monthColumnLabel={t('quickBooking.monthColumn')}
        yearColumnLabel={t('quickBooking.yearColumn')}
        openCalendarLabel={t('quickBooking.openCalendar')}
        prevMonthLabel={t('bookings.calendar.prevMonth')}
        nextMonthLabel={t('bookings.calendar.nextMonth')}
        dayAvailability={dayAvailability}
        disabled={disabled}
      />

      {mode === 'hour' ? (
        <div className={styles.twoCol}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="quick-booking-start">
              {t('quickBooking.entryTime')}
            </label>
            <RoundHourTimeInput
              id="quick-booking-start"
              value={startTime}
              language={language}
              hours={startHourOptions}
              disabled={disabled}
              ariaLabel={t('quickBooking.entryTime')}
              onChange={(next) => {
                setStartTime(next);
                setError(null);
              }}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="quick-booking-end">
              {t('quickBooking.exitTime')}
            </label>
            <RoundHourTimeInput
              id="quick-booking-end"
              value={endTime}
              language={language}
              hours={endHourOptions}
              disabled={disabled}
              ariaLabel={t('quickBooking.exitTime')}
              onChange={(next) => {
                setEndTime(next);
                setError(null);
              }}
            />
          </div>
        </div>
      ) : mode === 'month' ? (
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="quick-booking-months">
            {t('quickBooking.totalMonths')}
          </label>
          <input
            id="quick-booking-months"
            className={styles.numberInput}
            type="number"
            min={1}
            max={24}
            inputMode="numeric"
            value={monthCountInput}
            disabled={disabled}
            onChange={(event) => {
              const raw = event.target.value;
              if (raw === '' || /^\d+$/.test(raw)) {
                setMonthCountInput(raw);
                setError(null);
              }
            }}
            onBlur={() => {
              setMonthCountInput(String(parseBoundedInteger(monthCountInput, 1, 24, 1)));
            }}
          />
        </div>
      ) : (
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="quick-booking-days">
            {t('quickBooking.totalDays')}
          </label>
          <input
            id="quick-booking-days"
            className={styles.numberInput}
            type="number"
            min={1}
            max={30}
            inputMode="numeric"
            value={dayCountInput}
            disabled={disabled}
            onChange={(event) => {
              const raw = event.target.value;
              if (raw === '' || /^\d+$/.test(raw)) {
                setDayCountInput(raw);
                setError(null);
              }
            }}
            onBlur={() => {
              setDayCountInput(String(parseBoundedInteger(dayCountInput, 1, 30, 1)));
            }}
          />
        </div>
      )}

      {mode === 'hour' || showPeopleCount ? (
        <div className={styles.twoCol}>
          {mode === 'hour' ? (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('quickBooking.totalHours')}</span>
              <output className={styles.readonlyBox} aria-live="polite">
                {totalHours > 0 ? totalHours : '—'}
              </output>
            </div>
          ) : null}
          {showPeopleCount ? (
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="quick-booking-people">
                {t('quickBooking.people')}
              </label>
              <input
                id="quick-booking-people"
                className={styles.numberInput}
                type="number"
                min={1}
                max={peopleInputMax}
                inputMode="numeric"
                value={peopleInput}
                disabled={disabled}
                onChange={(event) => {
                  const raw = event.target.value;
                  if (raw === '' || /^\d+$/.test(raw)) {
                    setPeopleInput(raw);
                    setError(null);
                  }
                }}
                onBlur={() => {
                  setPeopleInput(String(parseBoundedInteger(peopleInput, 1, peopleInputMax, 1)));
                }}
              />
              <span className={styles.peopleHint}>{peopleLabel(people, language)}</span>
              {peopleCapacityAvailable !== null ? (
                <span className={styles.peopleHint}>
                  {t('quickBooking.peopleAvailable', { capacity: peopleCapacityAvailable })}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <p className={styles.exVatNote} role="note">
        {t('assetProfile.pricesExcludeVat')}
      </p>

      <div className={styles.totalRow}>
        <span className={styles.totalLabel}>{t('quickBooking.totalToPay')}</span>
        <span className={styles.totalValue}>{priceDisplay}</span>
      </div>
      {payableTotal !== null ? (
        <p className={styles.vatIncludedNote} role="note">
          {t('bookings.payment.vatIncluded')}
        </p>
      ) : null}

      {error || openingHoursError ? (
        <p ref={errorRef} className={styles.error} role="alert" tabIndex={-1}>
          {error ?? openingHoursError}
        </p>
      ) : null}

      <button
        type="button"
        className={styles.proceedBtn}
        disabled={
          disabled ||
          !assetId ||
          !dayKey ||
          subtotal === null ||
          subtotal < 1 ||
          selectedDayUnavailable ||
          Boolean(openingHoursError) ||
          (showPeopleCount && peopleInput.trim() === '') ||
          (mode === 'day' && dayCountInput.trim() === '')
        }
        title={disabled ? disabledTitle : undefined}
        onClick={handleProceed}
      >
        {t('quickBooking.proceedToPayment')}
      </button>
    </section>
  );
}
